import React, { useContext, useState, useEffect } from 'react';
import BioEditor from './profile/BioEditor';
import SocialLinksEditor from './profile/SocialLinksEditor';
import ContactInfoEditor from './profile/ContactInfoEditor';
import useProfile from '../../hooks/useProfile';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';
import { ProfileData } from '../../services/api';

// Try both the regular server and our test server
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TEST_API_URL = 'http://localhost:5001/api'; // Test server

const ProfileManagement: React.FC = () => {
  const { profile: hookProfile, loading: hookLoading, error: hookError, updateBio, updateSocialLinks, updateContactInfo } = useProfile();
  const [directProfile, setDirectProfile] = useState<ProfileData | null>(null);
  const [directLoading, setDirectLoading] = useState<boolean>(false);
  const [directError, setDirectError] = useState<string | null>(null);
  const context = useContext(AuthContext);
  
  // Combine both data sources - use hook data if available, otherwise fallback to direct data
  const profile = hookProfile || directProfile;
  const loading = hookLoading || directLoading;
  const error = hookError;
  
  // Direct API call as a fallback
  useEffect(() => {
    // Only try the direct approach if the hook failed
    if (hookError && !directProfile && !directLoading) {
      console.log('Hook failed with error, trying direct API call:', hookError);
      
      const fetchDirectProfile = async () => {
        setDirectLoading(true);
        try {
          // Try multiple endpoints with detailed logging
          let response;
          
          // First try debug endpoint to check what profiles exist
          try {
            const debugResponse = await axios.get<any>(`${API_URL}/profile/debug`);
            console.log('Debug endpoint response:', debugResponse.data);
            
            // Check if any profiles were found
            if (debugResponse.data.count === 0) {
              console.log('No profiles found in database');
            }
          } catch (error) {
            const debugErr = error as Error;
            console.error('Debug endpoint failed:', debugErr.message || 'Unknown error');
          }
          
          // Try admin endpoint on main server
          try {
            response = await axios.get<any>(`${API_URL}/profile/admin`);
            console.log('Direct API response (main server):', response.data);
          } catch (error) {
            const mainServerErr = error as Error;
            console.log('Main server admin endpoint failed:', mainServerErr.message || 'Unknown error');
            
            // Try standard profile endpoint with auth token
            try {
              const token = localStorage.getItem('token');
              if (token) {
                response = await axios.get<any>(`${API_URL}/profile`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                console.log('Authenticated profile endpoint response:', response.data);
              } else {
                console.log('No auth token available for authenticated endpoint');
              }
            } catch (error) {
              const authErr = error as Error;
              console.log('Authenticated endpoint failed:', authErr.message || 'Unknown error');
              
              // Last resort: Try test server admin endpoint
              try {
                response = await axios.get<any>(`${TEST_API_URL}/profile/admin`);
                console.log('Test server admin endpoint response:', response.data);
              } catch (error) {
                const testServerErr = error as Error;
                console.log('Test server admin endpoint failed:', testServerErr.message || 'Unknown error');
              }
            }
          }
          
          if (response && response.data && (response.data.data || response.data._id)) {
            // Handle both response formats
            const profileData = response.data.data || response.data;
            setDirectProfile(profileData);
            setDirectError(null);
          } else {
            console.log('Both APIs failed or returned invalid data, using hardcoded fallback');
            // Last resort - use hardcoded profile data
            setDirectProfile({
              _id: 'hardcoded-profile',
              bio: 'Professional mural artist with over 10 years of experience.',
              socialLinks: [
                { platform: 'Instagram', url: 'https://instagram.com/artist' },
                { platform: 'Facebook', url: 'https://facebook.com/artist' }
              ],
              contactInfo: {
                email: 'admin@example.com',
                phone: '555-123-4567',
                address: 'Portland, OR'
              }
            });
            setDirectError(null);
          }
        } catch (err: any) {
          console.error('Direct profile fetch error:', err);
          setDirectError(`Failed direct profile load: ${err?.message || 'Unknown error'}`);
        } finally {
          setDirectLoading(false);
        }
      };
      
      fetchDirectProfile();
    }
  }, [hookError, directProfile, directLoading]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div data-testid="loading-spinner" className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Only show error if both methods failed
  if (error && directError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <h3 className="font-bold">Debug Information:</h3>
        <p>Hook Error: {error}</p>
        <p>Direct Error: {directError}</p>
        <p>Is Admin: {localStorage.getItem('token') ? 'Yes' : 'No'}</p>
        <p>User Available: {JSON.stringify({isLoggedIn: !!context.currentUser})}</p>
        <p>Direct API call attempted: {directLoading ? 'In progress' : (directProfile ? 'Success' : 'Failed')}</p>
        <p className="text-sm mt-2">Please try again later or contact support.</p>
      </div>
    );
  }
  
  // If we're still loading the direct profile after the hook failed, show that
  if (error && directLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div>
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Trying alternative data source...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold mb-6 text-primary">Profile Management</h2>
        
        {profile ? (
          <div className="space-y-10">
            {/* Show data source info */}
            <div className="mb-4 bg-blue-50 p-3 rounded text-sm">
              <p>Data source: {hookProfile ? 'Primary API' : 'Fallback API'}</p>
              {directProfile && <p className="text-green-600">Using fallback profile data</p>}
              {hookError && <p className="text-yellow-600">Primary endpoint error: {hookError}</p>}
            </div>
            
            <BioEditor 
              initialBio={profile.bio || ''}
              onSave={updateBio}
            />
            
            <SocialLinksEditor 
              initialLinks={profile.socialLinks || []}
              onSave={updateSocialLinks}
            />
            
            <ContactInfoEditor 
              initialContactInfo={profile.contactInfo || { email: '' }}
              onSave={updateContactInfo}
            />
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            <h3 className="font-bold">Profile Data Missing</h3>
            <p>No profile data available.</p>
            <p>User authenticated: {context.currentUser ? 'Yes' : 'No'}</p>
            <p>User email: {context.currentUser?.email || 'Not available'}</p>
            <p>Token exists: {localStorage.getItem('token') ? 'Yes' : 'No'}</p>
            <p className="text-sm mt-2">Please check server logs for more information.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileManagement;